import { useEffect, useMemo, useRef } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import { useTexture } from "@react-three/drei";
import * as THREE from "three";
import { NOISE } from "../shaders";
import { audio } from "../../../lib/audio";

const ss = THREE.MathUtils.smoothstep;
const VERT = `varying vec2 vUv; void main(){ vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`;

// The storybook girl on her butterfly: drawn on the page, then lifting off and flying, wings beating.
const RIDER = `
uniform sampler2D uGirl;
uniform float uT;
varying vec2 vUv;
float hash2(vec2 p){ return fract(sin(dot(p, vec2(41.3, 289.1))) * 43758.5453); }
float ell(vec2 p, vec2 c, vec2 r, float a){ p -= c; p = mat2(cos(a), -sin(a), sin(a), cos(a)) * p; return length(p / r); }
void main(){
  vec2 p = (vUv - 0.5) * vec2(2.2, 1.4);
  float lift = smoothstep(8.5, 11.0, uT);
  float sq = mix(1.0, 0.3, lift * (0.5 + 0.5 * sin(uT * 4.2)));
  vec2 q = vec2(abs(p.x) / sq, p.y);
  float d = min(ell(q, vec2(0.5, 0.16), vec2(0.52, 0.34), -0.35), ell(q, vec2(0.36, -0.24), vec2(0.32, 0.27), 0.4));
  float wing = 1.0 - smoothstep(0.96, 1.0, d);
  float edge = smoothstep(0.86, 0.96, d);
  // organic cells, like poured paint: nearest of jittered points, dark veins between them
  vec2 gv = q * 5.5;
  vec2 gi = floor(gv);
  float d1 = 9.0, d2 = 9.0;
  vec2 best = vec2(0.0);
  for (int j = -1; j <= 1; j++) for (int i = -1; i <= 1; i++) {
    vec2 c = gi + vec2(float(i), float(j));
    vec2 pt = c + 0.5 + 0.4 * vec2(hash2(c) - 0.5, hash2(c + 7.0) - 0.5) * 2.0;
    float dd = length(gv - pt);
    if (dd < d1) { d2 = d1; d1 = dd; best = c; } else if (dd < d2) d2 = dd;
  }
  float h = hash2(best + 3.0);
  vec3 cell = h < 0.35 ? vec3(0.45, 0.72, 0.83) : h < 0.6 ? vec3(0.1, 0.14, 0.32) : h < 0.78 ? vec3(0.95, 0.96, 0.95) : h < 0.9 ? vec3(0.95, 0.5, 0.15) : vec3(0.74, 0.76, 0.78);
  cell *= 0.85 + 0.25 * smoothstep(0.6, 0.0, d1);
  vec3 col = mix(cell, vec3(0.06, 0.06, 0.08), max((1.0 - smoothstep(0.04, 0.12, d2 - d1)) * 0.9, edge));
  // coloured in with crayon, stroke by stroke
  float sid = floor((q.x * 0.8 + q.y) * 9.0 + 9.0);
  float coloured = step(sid * 0.28 + 0.6, uT);
  float crayon = 0.72 + 0.28 * step(0.45, fract((q.x * 0.8 + q.y) * 90.0 + hash2(floor(q * 40.0)) * 0.6));
  vec3 drawn = mix(vec3(0.98, 0.96, 0.9), col * crayon, coloured);
  drawn = mix(drawn, vec3(0.12), edge * (1.0 - coloured));
  col = mix(drawn, col, lift);
  float body = step(length(vec2(p.x / 0.05, (p.y + 0.02) / 0.42)), 1.0);
  col = mix(col, vec3(0.08, 0.07, 0.08), body);
  vec2 gu = (p - vec2(0.0, 0.2)) / vec2(0.62, 0.57) + 0.5;
  vec4 g = texture2D(uGirl, clamp(gu, 0.0, 1.0));
  float gIn = step(0.0, gu.x) * step(gu.x, 1.0) * step(0.0, gu.y) * step(gu.y, 1.0) * g.a * smoothstep(2.5, 4.5, uT);
  col = mix(col, g.rgb, gIn);
  float a = max(max(wing, body), gIn);
  if (a < 0.01) discard;
  gl_FragColor = vec4(col, a);
  #include <colorspace_fragment>
}`;

export function Rider({ clock }) {
  const girl = useTexture(`${process.env.PUBLIC_URL || ""}/arcs/butterfly/girl.webp`);
  const ref = useRef();
  const { size } = useThree();
  const mat = useMemo(() => {
    girl.colorSpace = THREE.SRGBColorSpace;
    return new THREE.ShaderMaterial({ vertexShader: VERT, fragmentShader: RIDER, uniforms: { uGirl: { value: girl }, uT: { value: 0 } }, transparent: true, depthWrite: false, side: THREE.DoubleSide });
  }, [girl]);
  useFrame(() => {
    if (!ref.current) return;
    const t = clock.current?.t ?? 0;
    mat.uniforms.uT.value = t;
    const lift = ss(t, 8.5, 11);
    const away = ss(t, 20.5, 24);
    const fit = Math.min(1, (size.width / size.height) * 2.1);
    ref.current.position.set(0, 1.55 - 0.45 * lift + 0.07 * Math.sin(t * 1.6) * lift + 1.6 * away, -2.6 + 0.45 * lift - 2.5 * away);
    ref.current.rotation.z = 0.05 * Math.sin(t * 0.9) * lift;
    ref.current.scale.setScalar(Math.max(0.0001, fit * (1 - 0.5 * away)));
    ref.current.visible = t < 24.5;
  });
  return (
    <mesh ref={ref} material={mat} renderOrder={5}>
      <planeGeometry args={[2.2, 1.4]} />
    </mesh>
  );
}

// The Earth, turning slowly; dragging turns only the globe, and every touch spreads green and a sound of life.
const GLOBE_VERT = `varying vec3 vN; varying vec3 vV; void main(){ vN = normalize(position); vV = normalize(normalMatrix * normal); gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`;
const GLOBE = `
uniform float uT;
uniform vec4 uTouch[12];
varying vec3 vN;
varying vec3 vV;
${NOISE}
void main(){
  vec3 n = normalize(vN);
  float sw = smoothstep(20.5, 24.0, uT);
  float a = sw * 5.0 * (1.0 - n.y * n.y);
  n = vec3(n.x * cos(a) - n.z * sin(a), n.y, n.x * sin(a) + n.z * cos(a));
  float land = snoise(n * 1.7 + 3.0) * 0.65 + snoise(n * 4.0) * 0.3;
  float green = 0.0;
  for (int i = 0; i < 12; i++){
    vec4 t = uTouch[i];
    if (t.w <= 0.0) continue;
    float r = 0.6 * smoothstep(0.0, 2.5, uT - t.w) + 0.001;
    green = max(green, smoothstep(cos(r), cos(r * 0.6), dot(n, normalize(t.xyz))));
  }
  vec3 ocean = mix(vec3(0.07, 0.24, 0.5), vec3(0.12, 0.38, 0.62), snoise(n * 6.0) * 0.5 + 0.5);
  vec3 forest = mix(vec3(0.11, 0.37, 0.28), vec3(0.3, 0.55, 0.25), snoise(n * 7.0) * 0.5 + 0.5);
  vec3 col = mix(ocean, forest, smoothstep(0.02, 0.08, land));
  col = mix(col, vec3(0.22, 0.62, 0.3), green * 0.8);
  col = mix(col, vec3(0.97), smoothstep(0.58, 0.82, snoise(vec3(n.x * 2.2 + uT * 0.03, n.y * 7.0, n.z * 2.2)) * 0.5 + 0.5) * 0.6);
  col *= 0.35 + 0.75 * max(0.0, dot(normalize(vV), normalize(vec3(-0.5, 0.5, 0.8))));
  col += vec3(0.4, 0.65, 1.0) * pow(1.0 - max(0.0, vV.z), 2.5) * 0.6;
  vec3 layers = mix(vec3(0.11, 0.37, 0.28), vec3(0.16, 0.21, 0.23), smoothstep(-0.3, 0.3, sin(n.y * 9.0 + snoise(n * 3.0) * 2.0)));
  layers = mix(layers, vec3(0.93), smoothstep(0.7, 0.9, snoise(n * 5.0) * 0.5 + 0.5) * 0.6);
  col = mix(col, layers, sw);
  gl_FragColor = vec4(col, 1.0 - smoothstep(22.5, 24.5, uT));
  #include <colorspace_fragment>
}`;

export function Globe({ clock, grab }) {
  const ref = useRef();
  const { size } = useThree();
  const S = useRef({ rot: 0, down: false, x: 0, n: 0, life: 0 });
  const mat = useMemo(
    () => new THREE.ShaderMaterial({ vertexShader: GLOBE_VERT, fragmentShader: GLOBE, uniforms: { uT: { value: 0 }, uTouch: { value: Array.from({ length: 12 }, () => new THREE.Vector4()) } }, transparent: true }),
    [],
  );
  useEffect(() => {
    const move = (ev) => {
      const s = S.current;
      if (!s.down) return;
      s.rot += (ev.clientX - s.x) * 0.006;
      s.x = ev.clientX;
    };
    const up = () => {
      S.current.down = false;
      grab.current = false;
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    return () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      mat.dispose();
    };
  }, [grab, mat]);
  useFrame((_, dt) => {
    if (!ref.current) return;
    const t = clock.current?.t ?? 0;
    const s = S.current;
    mat.uniforms.uT.value = t;
    if (!s.down) s.rot += 0.12 * dt;
    const k = ss(t, 9.5, 12.5);
    const end = ss(t, 20.5, 24.5);
    const fit = Math.min(1, (size.width / size.height) * 1.9);
    ref.current.rotation.y = s.rot;
    ref.current.position.set(0, 1.85 - (1 - k) * 2.5, -3.1);
    ref.current.scale.setScalar(Math.max(0.0001, 0.92 * k * (1 + end * 1.2) * fit));
    ref.current.visible = k > 0.001 && end < 0.999;
    window.__afaeGlobe = { rot: +s.rot.toFixed(2), touches: s.n };
  });
  const down = (e) => {
    const t = clock.current?.t ?? 0;
    if (t < 12 || t > 20.5) return;
    if (!ref.current || !e.point) return;
    e.stopPropagation();
    const s = S.current;
    s.down = true;
    s.x = e.nativeEvent.clientX;
    grab.current = true;
    const p = ref.current.worldToLocal(e.point.clone()).normalize();
    mat.uniforms.uTouch.value[s.n++ % 12].set(p.x, p.y, p.z, t);
    audio.life?.(s.life++ % 4);
    window.__afaeArcTaps = (window.__afaeArcTaps || 0) + 1;
  };
  return (
    <mesh ref={ref} material={mat} onPointerDown={down} visible={false}>
      <sphereGeometry args={[1.2, 96, 64]} />
    </mesh>
  );
}

// Her painted silhouette on the swing, hanging from the blossom branch; touch only ever moves the swing.
const SWING_FRAG = `
uniform sampler2D uMap;
uniform float uK;
varying vec2 vUv;
void main(){
  vec4 c = texture2D(uMap, vUv);
  float a = c.a * uK;
  if (a < 0.01) discard;
  gl_FragColor = vec4(c.rgb, a);
  #include <colorspace_fragment>
}`;

export function Swing({ clock, radius }) {
  const tex = useTexture(`${process.env.PUBLIC_URL || ""}/arcs/faith/swing.webp`);
  const pivot = useRef();
  const S = useRef({ ph: 0 });
  const mat = useMemo(
    () => {
      tex.colorSpace = THREE.SRGBColorSpace;
      return new THREE.ShaderMaterial({ vertexShader: VERT, fragmentShader: SWING_FRAG, uniforms: { uMap: { value: tex }, uK: { value: 0 } }, transparent: true, depthWrite: false });
    },
    [tex],
  );
  const th = 0.45 / (radius ?? 4.6);
  const r = (radius ?? 4.6) - 0.45;
  useFrame((_, dt) => {
    const c = clock.current;
    if (!c || !pivot.current) return;
    const t = c.t ?? 0;
    // energy from touch, easing away slowly; she always sways a little once the swing appears
    c.swing = Math.max(0, (c.swing || 0) - dt * 0.05);
    const amp = 0.08 + 0.55 * Math.min(1, c.swing);
    S.current.ph += dt * 1.9;
    pivot.current.rotation.z = amp * Math.sin(S.current.ph);
    mat.uniforms.uK.value = ss(t, 9.5, 11) * (1 - ss(t, 23, 26));
    window.__afaeSwing = +amp.toFixed(2);
  });
  return (
    <group position={[Math.sin(th) * r, 3.6, -Math.cos(th) * r]} rotation={[0, -th, 0]}>
      <group ref={pivot}>
        <mesh material={mat} position={[-0.24, -1.3, 0]} renderOrder={6}>
          <planeGeometry args={[1.74, 2.6]} />
        </mesh>
      </group>
    </group>
  );
}
